import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';

/** File content to preview: a file-server URL, or a blob downloaded from the API for files stored in the database. */
export interface MatFileViewerSource {
  url?: string;
  blob?: Blob | null;
}

export type ViewerKind = 'image' | 'frame' | 'video' | 'none';

export interface MatFileViewerData {
  fileName: string;
  fileIcon: string;
  source: Observable<MatFileViewerSource>;
  // Forces the preview type when the file name has no usable extension (e.g. inline comment images)
  kind?: ViewerKind;
}

/** Result returned when the user asks to download the file from the viewer. */
export const MAT_FILE_VIEWER_DOWNLOAD = 'download';

/** Dialog size shared by every place that opens the viewer. */
export const MAT_FILE_VIEWER_DIALOG_SIZE = { width: '80vw', maxWidth: '95vw', height: '90vh' };

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'];
const FRAME_EXTENSIONS = ['pdf', 'txt', 'csv', 'log'];
const VIDEO_EXTENSIONS = ['mp4', 'webm'];

@Component({
  selector: 'mat-file-viewer',
  templateUrl: './mat-file-viewer.component.html',
  styleUrls: ['./mat-file-viewer.component.scss']
})
/** 6963: popup that previews an attachment instead of opening it in a new browser tab */
export class MatFileViewerComponent implements OnInit, OnDestroy {

  kind: ViewerKind = 'none';
  loading = true;
  error = false;
  url?: string;
  safeUrl?: SafeResourceUrl;

  private objectUrl?: string;
  private sourceSubs?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<MatFileViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MatFileViewerData,
    private sanitizer: DomSanitizer
  ) { }

  /**
   * Picks the preview type from the file extension and loads the file content.
   * Files that cannot be previewed are not downloaded; the popup offers Download instead.
   */
  ngOnInit(): void {
    this.kind = this.data.kind ?? this.getKind(this.data.fileName);
    if (this.kind == 'none') {
      this.loading = false;
      return;
    }
    this.sourceSubs = this.data.source.subscribe(
      source => this.setSource(source),
      error => {
        console.error(error);
        this.error = true;
        this.loading = false;
      });
  }

  /** Cancels a pending load and releases the blob URL created for database-stored files. */
  ngOnDestroy(): void {
    this.sourceSubs?.unsubscribe();
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  /**
   * Builds the URL shown in the popup from the loaded file content.
   * @param source File-server URL, or blob from the API (wrapped in an object URL that is revoked on close).
   */
  private setSource(source: MatFileViewerSource) {
    if (source.blob) {
      this.objectUrl = URL.createObjectURL(source.blob);
      this.url = this.objectUrl;
    }
    else {
      this.url = source.url;
    }
    this.error = !this.url;
    // The URL comes from the ASCC file server or a local blob, so it is trusted for the iframe/video source
    this.safeUrl = this.url ? this.sanitizer.bypassSecurityTrustResourceUrl(this.url) : undefined;
    this.loading = false;
  }

  /**
   * @param fileName Attachment file name.
   * @returns How the file is rendered in the popup, based on its extension.
   */
  private getKind(fileName: string): ViewerKind {
    const extension = fileName?.split('.').pop()?.toLocaleLowerCase() ?? '';
    if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
    if (FRAME_EXTENSIONS.includes(extension)) return 'frame';
    if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
    return 'none';
  }

  /**
   * Opens the file in a new browser tab, as attachments opened before the popup existed.
   * For file types without a preview the content is loaded on demand; the tab is opened first so the browser does not block it as a popup.
   */
  openInNewTab() {
    if (this.url) {
      window.open(this.url, '_blank');
      return;
    }
    const tab = window.open('', '_blank');
    this.loading = true;
    this.sourceSubs?.unsubscribe();
    this.sourceSubs = this.data.source.subscribe(
      source => {
        this.setSource(source);
        if (tab && this.url) {
          tab.location.href = this.url;
        }
        else {
          tab?.close();
        }
      },
      error => {
        console.error(error);
        this.error = true;
        this.loading = false;
        tab?.close();
      });
  }

  /** Closes the popup and asks the attachment to run its existing download flow. */
  download() {
    this.dialogRef.close(MAT_FILE_VIEWER_DOWNLOAD);
  }

  close() {
    this.dialogRef.close();
  }
}
